// Composer.tsx — the bottom of the Sessions chat pane. Everything that
// shapes the next turn lives here — the text input, the tool set and the
// model — so the session list stays a pure navigator.
//
// With a session open: a note for a held message (with cancel), the
// Milkdown editor and Send. Always: the Tools button and the model picker,
// which with nothing selected chooses the model the next New session gets.
import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { BsSend, BsTools } from 'react-icons/bs';
import { MilkdownInput } from './MilkdownInput';
import ModelPicker from './ModelPicker';
import { sendButtonTitle } from './sessionFormat';
import type { QueuedMessage } from './turnState';
import type { ModelPicker as ModelPickerState } from './useModelPicker';

export interface ComposerProps {
  /** The open session's id, or null when nothing is selected. */
  selectedId: string | null;
  queued: QueuedMessage | null;
  onCancelQueued: () => void;
  input: string;
  onInputChange: (v: string) => void;
  onSend: (live?: string) => void;
  editorEpoch: number;
  streaming: boolean;
  sessionPhase: string | null;
  mcpEnabledCount: number;
  onOpenTools: () => void;
  picker: ModelPickerState;
}

const Composer: React.FC<ComposerProps> = ({
  selectedId, queued, onCancelQueued, input, onInputChange, onSend, editorEpoch,
  streaming, sessionPhase, mcpEnabledCount, onOpenTools, picker,
}) => {
  const hasSession = selectedId !== null;
  return (
    <div className="border-top p-2">
      {hasSession && (
        <>
          {/* A held message is the one thing the composer can do that
              leaves nothing on screen — the editor is already empty and the
              transcript has not moved. Say where it went, and give it back
              if the user changes their mind. */}
          {queued && queued.sessionId === selectedId && (
            <div className="d-flex align-items-center gap-2 mb-2 small text-muted">
              <Spinner animation="border" size="sm" />
              <span className="text-truncate" style={{ minWidth: 0 }}>
                Waiting for the session — will send “{queued.content}”
              </span>
              <Button
                size="sm"
                variant="link"
                className="text-muted p-0 flex-shrink-0"
                onClick={onCancelQueued}
              >
                cancel
              </Button>
            </div>
          )}
          {/* align-items-stretch, so Send is exactly as tall as the editor
              beside it — a 38px button pinned to the corner of a 10rem box
              reads as unrelated chrome rather than that box's action. */}
          <div className="d-flex align-items-stretch gap-2">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              {/* Never disabled. While a turn runs, sending steers it;
                  before the pod is up, sending queues. Taking the editor
                  away is the one response that loses what the user was in
                  the middle of saying. */}
              <MilkdownInput
                value={input}
                onChange={onInputChange}
                onSubmit={onSend}
                resetSignal={editorEpoch}
              />
            </div>
            <Button
              // Wrapped, not passed directly: React would hand the click
              // event in as `live` and it would be trimmed into nonsense.
              onClick={() => onSend()}
              disabled={!input.trim()}
              variant="primary"
              title={sendButtonTitle(streaming, sessionPhase)}
            >
              <BsSend />
            </Button>
          </div>
        </>
      )}
      {/* flex-wrap because the model box is as wide as the widest id the
          fleet serves and the divider makes this pane arbitrarily narrow —
          the picker drops below Tools rather than truncating. */}
      <div className={`d-flex flex-wrap align-items-center gap-2 ${hasSession ? 'mt-2' : ''}`}>
        <Button
          size="sm"
          variant="outline-light"
          onClick={onOpenTools}
          className="d-flex align-items-center gap-1"
          title="Configure which MCP servers are enabled for your sessions"
        >
          <BsTools /> Tools{mcpEnabledCount > 0 ? ` (${mcpEnabledCount})` : ''}
        </Button>
        <ModelPicker picker={picker} hasSession={hasSession} />
      </div>
    </div>
  );
};

export default Composer;
